import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

import { put } from 'helpers/api';
import confirm from 'components/confirm';
import AddControl from 'components/add_control';
import { Select } from 'components/ui/select';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import QuickbooksSelect from './select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons';

export default function VendorRelationTable({ users, relationType, showAll, vendorsById = {} }) {
  const userRows = showAll ? users : users.filter(u => u.quickbooksId);
  const initialRows = userRows.length === 0 && !showAll
    ? []
    : userRows.map(u => ({ id: `row-${u.id}`, userId: u.id, quickbooksId: u.quickbooksId }));
  const [rows, setRows] = useState(initialRows);
  const rowIdCounter = useRef(0);

  const [localUsers, setLocalUsers] = useState(users || []);
  const shouldOpenUserSelectModalOnLoad = rows.length === 0 && !showAll;
  const [showUserSelectModal, setShowUserSelectModal] = useState(shouldOpenUserSelectModalOnLoad);

  const selectedUserIds = rows.map(r => r.userId).filter(Boolean);
  const canAddUsers = selectedUserIds.length < localUsers.length;

  async function removeRow(rowId) {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;

    // If there's no associated QuickBooks ID, remove the row immediately
    // without prompting the user.
    if (!row.quickbooksId) {
      const updatedRows = rows.filter(r => r.id !== rowId);
      if (updatedRows.length === 0) {
        onNoRows();
        return;
      }
      setRows(updatedRows);
      return;
    }

    const confirmationMessage = 'Are you sure you want to delete this association? '
      + 'The vendor will remain in QuickBooks but they will be unlinked from this user.';
    try {
      await confirm(confirmationMessage, {
        title: `Delete Association?`,
        buttonLabel: 'Delete Association',
        buttonStyle: 'danger'
      });
    } catch (e) {
      return;
    }

    try {
      await updateUserQuickbooksId(row.userId, null);
      const updatedLocalUsers = localUsers.map(u => (u.id === row.userId ? { ...u, quickbooksId: null } : u));
      setLocalUsers(updatedLocalUsers);
      const updatedRows = rows.filter(r => r.id !== rowId);
      if (updatedRows.length === 0) {
        onNoRows();
        return;
      }

      setRows(updatedRows);
    } catch (e) {
      // Error handling for updateUserQuickbooksId failure
    }
  }

  function handleVendorChange(user, vendor) {
    const quickbooksId = vendor ? parseInt(vendor.id, 10) : null;
    updateUserQuickbooksId(user.id, quickbooksId).then(() => {
      setLocalUsers(prev => prev.map(u => (u.id === user.id ? { ...u, quickbooksId } : u)));
      setRows(prev => prev.map(r => (r.userId === user.id ? { ...r, quickbooksId } : r)));
    });
  }

  function updateUserQuickbooksId(userId, quickbooksId) {
    return put(`/users/${userId}`, { user: { quickbooks_id: quickbooksId } });
  }

  function onNoRows() {
    if (!showAll) {
      setRows([]);
    }
  }

  function renderUserSelectModal() {
    const selectedUserIds = rows.map(r => r.userId).filter(Boolean);

    function handleUserSelect(user) {
      if (user) {
        setRows((prev) => {
          rowIdCounter.current += 1;
          const newRowId = `new-row-${rowIdCounter.current}`;
          const updatedRows = [...prev, { id: newRowId, userId: user.id, quickbooksId: user.quickbooksId || null }];
          return updatedRows;
        });
      }
      setShowUserSelectModal(false);
    }

    return (
      <Modal open={true} onClose={() => setShowUserSelectModal(false)}>
        <Modal.Content centered>
          <Modal.Header>
            <Modal.Title>Add {relationType}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Select
              getOptionLabel={u => u.name}
              getOptionValue={u => u.id}
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              options={localUsers.filter(u => !selectedUserIds.includes(u.id))}
              placeholder="Select user to add"
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              onChange={handleUserSelect}
            />
          </Modal.Body>
        </Modal.Content>
      </Modal>
    );
  }

  if (localUsers.length === 0) {
    return <p>No users found for this company.</p>;
  }


  const relationTitle = `${relationType} in Buildout`;

  return (
    <>
      <table className="table" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ width: '50%' }}>{relationTitle}</th>
            <th style={{ width: showAll ? '50%' : 'auto' }}>QuickBooks Vendor</th>
            {!showAll && <th style={{ width: '3rem'  }} />}
          </tr>
        </thead>
        <tbody>
          {rows.map((user) => {
            const currentUser = localUsers.find(u => u.id === user.userId);
            const currentVendor = vendorsById[currentUser?.quickbooksId];

            return (
              <tr key={user.id}>
                <td
                  className="align-middle"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {currentUser?.name || ''}
                </td>
                <td>
                  <QuickbooksSelect
                    buildoutObject={currentUser}
                    hydrated
                    initialValue={currentVendor}
                    quickbooksId={currentUser?.quickbooksId}
                    type="vendor"
                    onChange={vendor => handleVendorChange(currentUser, vendor)}
                  />
                </td>
                {!showAll && (
                  <td className="align-middle">
                    <div className="d-flex gap-2">
                      {currentUser && (
                        <FontAwesomeIcon
                          className="clickable text-destructive"
                          icon={faTrashAlt}
                          onClick={() => removeRow(user.id)}
                        />
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
          {!showAll && canAddUsers && (
            <tr>
              <td colSpan={3}>
                <AddControl onClick={() => setShowUserSelectModal(true)}>
                  Add Associations
                </AddControl>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {showUserSelectModal && renderUserSelectModal()}
    </>
  );
}

VendorRelationTable.propTypes = {
  relationType: PropTypes.string.isRequired,
  showAll: PropTypes.bool.isRequired,
  users: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    quickbooksId: PropTypes.number,
  })).isRequired,
  vendorsById: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayName: PropTypes.string,
  })),
};
